/**
 * Keeps the cart's free add-on lines in step with the items that earn them — a sunglass case
 * for every pair of sunglasses, matching quantity as the shopper changes it.
 *
 * None of the rules live here. `snippets/free-product-work.liquid` reads them off
 * `custom.free_product` and answers with the lines to add, change or drop; this carries that
 * out. A product added since page load is covered because the answer is re-read from
 * `sections/free-product-sync.liquid` on every cart change, not computed from a config
 * captured at render time.
 */
import { Component } from '@theme/component';
import { addItemsToCart, changeCartLine, refreshCart } from '@theme/cart-utils';
import { FREE_PRODUCT_LINE_PROPERTIES } from '@theme/line-item-properties';

/** @typedef {{ variantId: number, required: number, lineKey: string, quantity: number }} FreeProductWork */

const SECTION_ID = 'free-product-sync';

class FreeProduct extends Component {
  /** @type {FreeProductWork[]} */
  #work = [];

  /** Guards the reconcile against the cart update its own writes produce. */
  #reconciling = false;

  connectedCallback() {
    super.connectedCallback();

    // Seeded, not acted on. A cart write as the page becomes interactive queues the shopper's
    // next add behind it, since Shopify serialises cart writes per session — so a cart is
    // brought into step the first time it changes rather than on arrival.
    this.#work = readWork(this);

    document.addEventListener('cart:update', this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:update', this.#onCartUpdate);
  }

  /** @param {Event} event */
  #onCartUpdate = async (event) => {
    const { detail } = /** @type {CustomEvent} */ (event);
    if (detail?.data?.didError) return;
    if (this.#reconciling) return;
    if (window.Shopify?.designMode) return;

    this.#work = await this.#fetchWork();

    const pending = this.#work.filter((entry) => entry.required !== entry.quantity);
    if (!pending.length) return;

    this.#reconciling = true;

    try {
      for (const entry of pending) {
        if (entry.lineKey) {
          // Covers the drop too: a required of zero takes the line off.
          await changeCartLine(entry.lineKey, { quantity: entry.required });
        } else {
          await addItemsToCart([
            {
              variantId: entry.variantId,
              quantity: entry.required,
              properties: FREE_PRODUCT_LINE_PROPERTIES,
            },
          ]);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.#reconciling = false;
    }

    // The cart on screen was rendered before these lines moved, and the write that moved them
    // announces nothing. The update this produces finds the work settled and stops there.
    await refreshCart({ source: 'free-product' }).catch((error) => console.error(error));
  };

  /**
   * Re-reads the work from the server, against the cart as it now stands.
   *
   * @returns {Promise<FreeProductWork[]>}
   */
  async #fetchWork() {
    const response = await fetch(`${window.location.pathname}?section_id=${SECTION_ID}`).catch(() => null);
    if (!response?.ok) return [];

    const html = await response.text();

    return readWork(new DOMParser().parseFromString(html, 'text/html'));
  }
}

/**
 * @param {ParentNode} root
 * @returns {FreeProductWork[]}
 */
function readWork(root) {
  const json = root.querySelector('script[data-free-product-work]')?.textContent;

  try {
    return JSON.parse(json || '[]');
  } catch {
    return [];
  }
}

if (!customElements.get('free-product')) {
  customElements.define('free-product', FreeProduct);
}
